import {
  AcceptInvitationModel,
  APIs,
  Config,
  LoginRequestModel,
  AuthUser,
  PasswordResetModel,
  SimpleUserModel,
  VerifyInvitationModel,
} from '@ecdlink/core';
import { NewPasswordRequest } from '@models/auth/login/NewPasswordRequest';
import { PasswordResetRequestReceived } from '@models/auth/login/PasswordResetRequestReceived';
import { SignUpInviteVerify } from '@models/auth/sign-up/SignUpInviteVerify';
import { getDataResponse } from '@utils/common/data-response.utils';
import { api } from '../axios.helper';

const handlerError = (error: any) => {
  return error?.response;
};

const headers = { 'Content-Type': 'application/json' };

class AuthService {
  async login(
    baseEndPoint: string,
    body: LoginRequestModel
  ): Promise<AuthUser> {
    const apiInstance = api(baseEndPoint);
    const response = await apiInstance.post(
      APIs.authLogin,
      JSON.stringify(body),
      {
        headers: headers,
      }
    );

    if (response.status !== 200) {
      throw new Error('Login failed - Server connection error');
    }

    return response.data;
  }

  async VerifyInvitationRequest(
    verifyInvitationModel: VerifyInvitationModel
  ): Promise<SignUpInviteVerify> {
    const BASE_URL = Config.authApi;

    const response = await api(BASE_URL)
      .post(APIs.verifyInvitation, JSON.stringify(verifyInvitationModel))
      .catch(handlerError);

    const dataResponse = getDataResponse<boolean>(response);

    if (dataResponse.dataError) {
      return {
        verified: false,
        errorMessage: dataResponse.dataError.error,
        errorCode: dataResponse.dataError.errorCode,
      };
    }

    return {
      verified: true,
    };
  }

  async AcceptInvitationRequest(
    acceptInvitationModel: AcceptInvitationModel
  ): Promise<boolean> {
    const BASE_URL = Config.authApi;
    const response = await api(BASE_URL)
      .post(APIs.acceptInvitation, JSON.stringify(acceptInvitationModel))
      .catch(handlerError);

    const dataResponse = getDataResponse<boolean>(response);

    if (dataResponse.dataError) return false;

    return true;
  }

  async SendForgotPasswordRequest(
    simpleUserModel: SimpleUserModel
  ): Promise<PasswordResetRequestReceived> {
    const requestSentResponse = await api(Config.authApi)
      .post(APIs.forgotPassword, JSON.stringify(simpleUserModel))
      .catch(handlerError);

    const dataResponse =
      getDataResponse<PasswordResetRequestReceived>(requestSentResponse);

    if (dataResponse.dataError)
      return {
        errorMessage: dataResponse.dataError.error,
        valid: false,
      };

    return {
      phoneNumber: dataResponse.data?.phoneNumber,
      returnUrl: dataResponse.data?.returnUrl,
      valid: true,
    };
  }

  async SendNewPasswordRequest(
    newPasswordModel: PasswordResetModel
  ): Promise<NewPasswordRequest> {
    const requestSentResponse = await api(Config.authApi)
      .post(APIs.confirmForgotPasswordReset, JSON.stringify(newPasswordModel))
      .catch(handlerError);

    const dataResponse =
      getDataResponse<NewPasswordRequest>(requestSentResponse);

    if (dataResponse.dataError)
      return {
        errorMessage: dataResponse.dataError.error,
        valid: false,
      };

    return {
      valid: true,
    };
  }

  async SendAuthCode(username: string, token: string): Promise<boolean> {
    const BASE_URL = Config.authApi;
    const response = await api(BASE_URL).post(
      APIs.sendAuthCode,
      JSON.stringify({
        token,
        username,
      })
    );

    const dataResponse = getDataResponse<boolean>(response);

    if (dataResponse.dataError) return false;

    return true;
  }

  async RefreshToken(currentToken: string): Promise<AuthUser | undefined> {
    const BASE_URL = Config.authApi;

    const response = await api(BASE_URL)
      .post(
        `${APIs.refreshJwtToken}`,
        JSON.stringify({
          token: currentToken,
        })
      )
      .catch(handlerError);
    if (response.status !== 200) {
      throw new Error('Getting refresh token failed - Server connection error');
    }

    return response.data;
  }
}

export default AuthService;
